import { cart } from "@/theme/Cart";
import { CartMain } from '../Cart/CartMain';
import { Component, Prop, Ref, Watch } from "@eastsideco/salvo-ts";
import { debounce } from 'lodash';
import { ProductOptionPicker } from "./ProductOptionPicker";
import { QuantitySelector } from "../QuantitySelector/QuantitySelector";
import { setVariantUrlParam, getQuantityMax } from "@/utils/products";
import { formatMoney } from "@/utils/formatting";
import { SideCart } from '../SideCart/SideCart';
import { ThemeComponent } from "@/theme/ThemeComponent";
import { ProductGallery } from "./ProductGallery";
import { ShopifyCartItemInput } from "@eastsideco/salvo-ts/lib/shopify/AjaxApi";
import { SimpleEvent } from "@eastsideco/salvo-ts";
import { throttle } from 'lodash';

/**
 * A form which allows customers to select product options and then
 * add the matching variant and quantity to the cart.
 */
interface Builds {
    id: number;
    buildImages: [
        {
            ids: string[];
            src: string;
        }
    ];
}

@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop optionStyle!: 'dropdown' | 'buttons';
    @Prop variantsQty!: string;
    @Prop strings!: string;
    @Prop builds!: Builds[];
    @Ref options!: ProductOptionPicker;
    @Ref submitButton!: HTMLButtonElement[];
    @Ref error!: HTMLElement;
    @Ref price!: HTMLElement;
    @Ref({ required: false }) comparePrice!: HTMLElement;
    @Ref sku!: HTMLElement;
    @Ref({ required: false }) quantity!: QuantitySelector | null;
    @Ref({ required: false }) drawerToggle!: HTMLButtonElement;
    @Ref({ required: false }) stock!: HTMLParagraphElement;
    @Ref summary!: HTMLDivElement;
    @Ref pricetotal!: HTMLSpanElement;
    @Ref selectedValue!: HTMLSpanElement;
    @Ref({ required: false }) editButton: HTMLElement | null = null;

    // gift card fucntionality

    @Ref({ required: false }) giftCheckbox!: HTMLInputElement;
    @Ref({ required: false }) recipientEmail!: HTMLInputElement;
    @Ref({ required: false }) recipientName!: HTMLInputElement;
    @Ref({ required: false }) recipientMessage!: HTMLInputElement;
    @Ref({ required: false }) recipientDate!: HTMLInputElement;
    @Ref({ required: false }) recipientMessageRemainingValue!: HTMLSpanElement;

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant | null = null;
    protected quantityVal: number = 1;
    protected maxQty: string = '1';
    protected selectedCartQty: number = 0;
    private cartMain: CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart: SideCart | null = document.body.querySelector('side-cart');
    private gallery = document.querySelector('product-gallery') as ProductGallery;
    private formHasloaded: boolean = false;

    private _onVariantChange = new SimpleEvent<Shopify.Liquid.Variant>();

    public get onVariantChange() {
        return this._onVariantChange.asEvent();
    }

    protected giftcardFormNamePresent = false;
    protected giftcardFormEmailPresent = false;

    init() {
        this.handlePreSelected();

        // On variant change
        this.$subscribe(this.options.onSelectedVariantChange, (variant) => {
            // Set the selected variant
            this.selectedVariant = variant;
            if (this.selectedVariant) {
                // Dispatch an event with the selected variant
                this._onVariantChange.dispatch(this.selectedVariant);
            }
        });
        this.selectedVariant = this.options.selectedVariant;

        // Quantity Funtions
        this.setQuantityMax();
        document.addEventListener('cartChanged', (e: Event) => {
            this.setQuantityMax();
        });

        this.checkQuantityChange();

        // Attach event listeners to all submit buttons
        for (const button of this.submitButton) {
            this.$listen(button, 'click', async() => {
                await this.addToCart();
            });
        }

        if (this.drawerToggle) {
            this.$listen(this.drawerToggle, 'click', (event) => {
                event.preventDefault();
                document.dispatchEvent(new Event('openOptionDrawer'));
            });
        }
        this.buildSummary();
        document.addEventListener('buildProdFormSummary', () => {
            this.buildSummary();
        });

        document.addEventListener('addonUpdated', () => {
            this.buildSummary();
        });

        // Validate gift recipient message
        if (this.recipientMessage) {
            this.$listen(this.recipientMessage, 'keyup', (event) => {
                if (this.recipientMessageRemainingValue) {
                    this.recipientMessageRemainingValue.innerText = `${200 - this.recipientMessage.value.length}`;
                }
            });
        }
    }

    protected showError(msg: string | null): void {
        this.error.textContent = msg;
        this.error.classList.add('is-active');
        this.error.style.display = msg ? '' : 'none';
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        if (this.formHasloaded) {
            setVariantUrlParam(this.selectedVariant?.id || null);
        }
        this.formHasloaded = true;
        this.setQuantityMax();
        this.handlePrice();
        this.handleSku();
        this.buildSummary();
    }

    @Watch('loading')
    protected handleLoading() {
        for (const button of this.submitButton) {
            button.disabled = this.loading;
        }
    }

    /**
     *  Update Price;
     * @returns
     */
    handlePrice() {
        if (this.selectedVariant === null) { return; }
        const updatedPrice = formatMoney(this.selectedVariant.price) as string;
        const updatedComparePrice = formatMoney(this.selectedVariant.compare_at_price) as string;

        if (updatedPrice === null) { return; }
        this.price.innerHTML = updatedPrice;

        if (this.comparePrice !== null) {
            if (updatedComparePrice) {
                this.comparePrice.classList.remove('visually-hidden');
                this.comparePrice.innerHTML = updatedComparePrice;
            } else {
                this.comparePrice.classList.add('visually-hidden');
            }
        }
    }
    /**
         *  Update SKU;
         * @returns
         */
    handleSku() {
        if (this.selectedVariant === null) { return; }
        const updatedSku = this.selectedVariant.sku as string;

        if (updatedSku === null) { return; }
        this.sku.innerHTML = updatedSku;
    }
    /**
     * Set maximum variant quantity on variant change.
     */
    protected setQuantityMax() {
        this.maxQty = getQuantityMax(this.selectedVariant?.id || null, this.variantsQty);
        this.selectedCartQty = this.checkCartQty(this.selectedVariant);
        if (this.selectedCartQty > 0) {
            this.maxQty = String(~~this.maxQty - this.selectedCartQty);
        }

        this.handleQtyInput();
        this.handleQtyButtons();

        if (~~this.maxQty === 0) {
            this.handleOutOfStock();
            if (!this.quantity) { return; }
            this.quantity?.input.setAttribute('max', this.maxQty);
            this.quantity?.classList.add('visually-hidden');
            return;
        }
        this.handleInStock();
        if (!this.quantity) { return; }
        this.quantity?.input.setAttribute('max', this.maxQty);
        this.quantity?.classList.remove('visually-hidden');

    }

    handleQtyInput() {
        const qtyInput = this.quantity?.input.value;

        if (qtyInput && (~~qtyInput > ~~this.maxQty)) {
            if (!this.quantity) { return; }
            this.quantity.input.value = this.maxQty;
            return;
        }
    }

    handleQtyButtons() {
        if (!this.quantity) { return; }

        if (~~this.quantity.input.value >= ~~this.maxQty) {
            this.quantity.increaseButton.disabled = true;
            return;
        }

        this.quantity.increaseButton.disabled = false;
    }

    /**
     * Check if selected variant is in cart? get quanity.
     * @param variant - selected variant.
     * @returns
     */
    protected checkCartQty(variant: Shopify.Liquid.Variant | null) {
        if (variant === null || cart.items.length === 0) { return 0; }

        let quantity = 0;
        for (const item of cart.items) {
            if (item.id === variant.id) {
                quantity = item.quantity;
            }
        }

        return quantity;
    }

    /**
     * If quantity switcher exists, update Quantity on value change.
     */
    protected checkQuantityChange() {
        if (this.quantity === null) { return; }
        this.$subscribe(this.quantity.onChange, (value) => {
            this.quantityVal = value;
        });
    }

    /**
     * Add product to cart.
    */
    protected async addToCart() {

        // Check gift card conditions - do not continue if they fail
        let giftCardConditionsPassed = true;
        if (this.giftCheckbox) {
            giftCardConditionsPassed = await this.checkGiftCardConditions();
        }
        if (!giftCardConditionsPassed) {
            return;
        }

        // Attempt add to cart
        try {

            // Check variant exists
            const variant = this.selectedVariant;
            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            // Define line item properties shape
            const properties: {[key: string]: string} = {};

            // Ensure Add-on products are added to cart
            // in the same request
            const ids = this.summary.querySelectorAll('input[name="id"]') as unknown as HTMLInputElement[];
            const idsToAdd = [];
            const cartCheck: { id: number; inCart: boolean }[] = [];
            for (const [i, id] of ids.entries()) {
                const idUse = parseInt(id.value, 10);
                const itemInCart: boolean = cart.checkCart(idUse);
                const addString = { id: idUse, quantity: this.quantityVal, properties: properties};
                const checkString = { id: idUse, inCart: itemInCart };
                cartCheck.push(checkString);
                idsToAdd.push(addString);
            }

            // Check if gift checkbox checked
            if (this.giftCheckbox && this.giftCheckbox.checked) {
                properties['__shopify_send_gift_card_to_recipient'] = this.giftCheckbox.value;
                properties['Recipient email'] = this.recipientEmail.value;
                properties['Recipient name'] = this.recipientName.value;
                properties['Send on'] = this.recipientDate.value;
                properties['Message'] = this.recipientMessage.value;
            }

            await cart.addItems(idsToAdd)
                .then((items) => {
                // Dispatch event for other components to listen to
                    document.dispatchEvent(new Event('cartChanged'));
                })
                .catch((event: unknown | null) => {
                    if (event instanceof Error) {
                        this.showError(event.message);
                    }
                });


        // Handle errors
        } catch (event) {

            // Log and show FE error
            this.$log.error('Cart error', { event });
            const { message } = event as Error;
            this.showError(message);
        }
    }

    /**
     * Diasable AdToCart if variant is out of stock.
     */
    protected handleOutOfStock() {
        for (const button of this.submitButton) {
            button.disabled = true;
            button.classList.add('button--disabled');
        }
        this.stock.textContent = this.getString('outOfStock');
        for (const button of this.submitButton) {
            const span = button.querySelector('span');
            if (span) {
                span.textContent = this.getString('soldOut');
            }
        }
    }

    /**
     * Enable AddToCart.
     */
    protected handleInStock() {
        for (const button of this.submitButton) {
            button.disabled = false;
            button.classList.remove('button--disabled');
        }
        this.stock.textContent = this.getString('inStock');
        for (const button of this.submitButton) {
            const span = button.querySelector('span');
            if (span) {
                span.textContent = this.getString('add');
            }
        }
    }

    /**
     * Get Language string set within the product template.
     * @param string
     * @returns
     */
    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }

    buildSummary() {
        const pickers = this.querySelectorAll('product-option-picker[data-picker="true"]') as
        unknown as ProductOptionPicker[];
        const isSIngleItem = document.querySelector('.product-form')?.getAttribute('is-single-item');
        let html = '<div class="product__summary-items">';
        let count = 0;
        let total = 0;
        const ids = [];
        for (let index = 0; index < pickers.length; index++) {
            const picker = pickers[index];
            const product = picker.product;
            const variant = picker.selectedVariant;

            const elements = document.querySelectorAll('product-option-picker[data-picker="false"]');
            for (const element of elements) {
                const pickerOptionType = element
                    .querySelector('.product-form__button-options')
                    ?.getAttribute('name');

                if (pickerOptionType?.toLowerCase() !== 'color') {
                    continue; // Skip to the next iteration
                }
            }

            if (variant && variant?.available) {
                const none = picker.querySelector(
                    `[data-ref="noSelectOption"][data-title="${product.handle}"].is-active`
                );
                if (!none) {
                    const spanClass = index === 0 ? 'editVariant' : 'editAddOn';
                    html += `
                        <div class="product__summary-item flex f-vertical-center f-space-between">
                        <p>${variant.name}</p>
                        <p>${formatMoney(variant.price) as string} <span class="${spanClass}" >Edit</span></p>
                        <input type="hidden" value="${variant.id}" name="id">
                        </div>
                    `;
                    count = count + 1;
                    total = total + variant.price;
                    ids.push(variant.id);
                }

                if (isSIngleItem == 'true') {
                    html += `<input type="hidden" value="${variant.id}" name="id">`;
                    total = total + variant.price;
                    ids.push(variant.id);
                    break;
                }
            }
        }

        // get selected addons in
        const addons = document.querySelectorAll('.product-form__button-item--addons.is-active');
        for (const addon of addons) {
            const addonTitle = addon.querySelector('.product-form__selected-option')?.textContent || 'Addon';
            const addonPrice = parseFloat(
                addon.querySelector('.product-form__button-price')?.textContent?.replace(/[^0-9.-]+/g, '') || '0'
            ) * 100;
            const addonId = addon.getAttribute('data-optionId');

            if (addonId && addonPrice) {
                const idFormatted = addonId.replace(/^[^-]+-/, '');
                html += `
                    <div class="product__summary-item product__summary-item--addons 
                    flex f-vertical-center f-space-between">
                    <p>${addonTitle}</p>
                    <p>${formatMoney(addonPrice)} <span class="editAddOn">Edit</span></p>
                    <input type="hidden" value="${idFormatted}" name="id">
                    </div>
                `;
                count = count + 1;
                total += addonPrice;
                ids.push(parseInt(idFormatted, 10));
            }
        }


        html += `
			<hr>
			<div class="product__summary-item flex f-vertical-center f-space-between">
				<p>Taxes</p>
				<p>Calculated at checkout</p>
			</div>
			<div class="product__total-row flex f-vertical-center f-space-between">
				<p>Total</p>
				<p>${formatMoney(total) as string}</p>
			</div>
			</div>
		`;

        this.classList.toggle('product-form--summary-hidden', count <= 1);
        // Correctly handle class changes for each button
        for (const button of this.submitButton) {
            button.classList.remove('button--secondary');
            button.classList.add('button--primary');
        }

        const variantContent = document.querySelector('.product-option-picker')?.getAttribute('variant-content');
        const variantData = JSON.parse(variantContent as string);
        const optionName = document.querySelector('.option_name') as HTMLElement;
        const errorMessage = document.querySelector('.product-main__error') as HTMLElement;
        const fromText = document.querySelector('.fromText') as HTMLElement;

        if (total === 0 && variantData.length > 1) {
            for (const button of this.submitButton) {
                button.disabled = true;
                button.children[0].textContent?.replace(/\|/g, '');
                // Hide optionName when buttons are disabled
                if (optionName) {
                    optionName.style.display = 'none';
                }
                if (errorMessage) {
                    errorMessage.style.display = '';
                }
                if (fromText) {
                    fromText.style.display = '';
                }
            }
        } else {
            for (const button of this.submitButton) {
                button.disabled = false;
                const span = button.querySelector('span');
                if (span) {
                    // Add "|" only if the button is not part of 'product-sticky__actions__submit'
                    if (button.classList.contains('product-sticky__actions__submit')) {
                        span.textContent = this.getString('add');
                    } else {
                        span.textContent = `${this.getString('add')} |`;
                    }
                }
            }
            if (optionName) {
                optionName.style.display = ''; // Remove the display style to revert to default
            }
            if (errorMessage) {
                errorMessage.style.display = 'none';
            }
            if (fromText) {
                fromText.style.display = 'none';
            }
            if (this.editButton) {
                this.editButton.classList.remove('product-sticky__actions__edit--hide-edit');
            }
        }


        this.summary.innerHTML = html;
        const priceTotalElements = document.querySelectorAll<HTMLSpanElement>('[data-ref="pricetotal"]');
        for (const element of priceTotalElements) {
            element.textContent = formatMoney(total) as string;
        }
        this.imageUpdates(ids);

        // Add event listener to "Edit" buttons
        this.addEditButtonListeners();
        this.addEditVaraintListeners();
    }

    imageUpdates(ids: number[]) {
        ids = ids.filter((id) => id != this.selectedVariant?.id);
        ids.sort();
        const buildItems = this.builds.find((i: Builds) => i.id == this.selectedVariant?.id);
        if (!buildItems) {
            this.gallery.setHoldImage();
            return;
        }
        let found = false;
        for (const [i, build] of buildItems.buildImages.entries()) {
            const buildIDs = build.ids.map((x) => +x);
            buildIDs.sort();
            const idsString = ids.join('-');
            const buildIdsString = buildIDs.join('-');
            if (idsString == buildIdsString) {
                const img = this.gallery.first.querySelector('img');
                if (build.src && img) {
                    img.src = build.src;
                    img.height = 0;
                    img.width = 0;
                    found = true;
                }
            }
        }
        if (!found) {
            this.gallery.setHoldImage();
        }

    }

    handlePreSelected() {
        const urlParams = new URLSearchParams(window.location.search);
        const variant = urlParams.get('variant');
        if (variant) {
            document.querySelector<HTMLElement>(
                `.product-form__button-item.radio__container.is-active`
            )?.click();
        }
    }
    /**
     * Check Gift Card Inputs
     */

    private async checkGiftCardConditions() {

        // Assume conditions pass
        let conditionsPass = true;

        // Check if gift checkbox checked
        if (this.giftCheckbox.checked) {

            // Check recipient name is entered
            if (this.recipientName.value.length < 1) {

                // Tell function conditional checks have failed
                conditionsPass = false;

                // Check for previous error not removed, create new error
                const existingError = document.querySelector('.field--error-name');
                if (existingError === null) {
                    const errorElement = document.createElement('span');
                    errorElement.classList.add('field--error-name');
                    errorElement.textContent = 'Please enter the recipients name';
                    this.recipientName.after(errorElement);
                }
            }

            // Check recipient email is entered
            if (this.recipientEmail.value.length < 1) {

                // Tell function conditional checks have failed
                conditionsPass = false;

                // Check for previous error not removed, create new error
                const existingError = document.querySelector('.field--error-email');
                if (existingError === null) {
                    const errorElement = document.createElement('span');
                    errorElement.classList.add('field--error-email');
                    errorElement.textContent = 'Please enter the recipients email';
                    this.recipientEmail.after(errorElement);
                }
            }
        }

        // Return value
        return conditionsPass;
    }

    /**
     * Handle errors
     */

    private hideErrorsOnInput() {

        // Define an array of inputs that will have associated errors
        const inputs = [
            this.recipientEmail,
            this.recipientName
        ];

        // Loop through inputs, listen for change
        for (const input of inputs) {
            if(input != undefined) {
                this.$listen(input, 'input', throttle(() => {
                    const error = input.parentNode?.querySelector('[class*="field--error"]');
                    if (error) {
                        error.remove();
                    }
                }, 500));
            }
        }
    }

    private addEditVaraintListeners() {
        const editVariant = this.summary.querySelectorAll('.editVariant') as NodeListOf<HTMLSpanElement>;
        for (const button of editVariant) {
            this.$listen(button, 'click', (event) => {
                event.preventDefault();
                document.dispatchEvent(new Event('openOptionDrawer'));
            });
        }
    }

    private addEditButtonListeners() {
        const editButtons = this.summary.querySelectorAll('.editAddOn') as NodeListOf<HTMLSpanElement>;
        const drawer = document.querySelector('.product-form__add-on-drawer') as HTMLDivElement;

        for (const button of editButtons) {
            this.$listen(button, 'click', (event) => {
                event.preventDefault();
                this.toggleDrawer(drawer);
            });
        }
    }

    toggleDrawer(drawer: HTMLElement) {
        const isOpen = drawer.getAttribute('open') === 'true';
        const bodyOverlay = document.querySelector('.body-overlay');
        if (isOpen) {
            drawer.removeAttribute('open'); // Remove the attribute when closing the drawer
            drawer.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('drawer-open'); // Remove class from body
            if (bodyOverlay) {
                bodyOverlay.classList.remove('visible');
                setTimeout(() => {
                    bodyOverlay.classList.add('hidden');
                }, 300);
            }
        } else {
            drawer.setAttribute('open', 'true'); // Set the attribute when opening the drawer
            drawer.setAttribute('aria-hidden', 'false');
            document.body.classList.add('drawer-open'); // Add class to body
            if (bodyOverlay) {
                bodyOverlay.classList.remove('hidden');
                setTimeout(() => {
                    bodyOverlay.classList.add('visible');
                }, 10);
            }
        }
        // Toggle CSS class for animations
        drawer.classList.toggle('is-open', !isOpen);
    }
}
